import styles from './styles.module.scss';
import { DownOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import { STORAGE_KEYS } from '@/config/constants';
import { routes } from '@/config/routes';
import { useAuth } from '@/context/AuthContext';
import { Account, UserRole } from '@/types';
import type { SwitchableAccount } from '@/types/auth';
import { getAccounts, getTemporaryAccess } from '@/pages/CommandCenter/Accounts/services';
import { getSwitchableAccounts, switchAccount } from '@/services/accountSwitch.service';
import { applySwitchedSession, groupSwitchableAccounts } from '@/utils/accountSwitching';
import { toast } from 'react-toastify';
import { CustomMap } from '@/types/ICustomMap';

type Props = {
    accountName: string;
    accountId: string;
}

const AccountDetails = ({ accountName, accountId }: Props) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const { user } = useAuth();

    const isSuperAdmin = useMemo(() => user?.role === UserRole.SUPER_ADMIN, [user]);

    // Token-free listing — a token is minted only for the account actually entered.
    const { data: accounts } = useQuery({
        queryKey: ['switchable-accounts'],
        queryFn: getSwitchableAccounts,
        placeholderData: (prev) => prev,
    });

    const { data: allAccounts } = useQuery({
        queryKey: ['accounts'],
        queryFn: () => getAccounts({ limit: 400 }),
        enabled: isSuperAdmin,
    });

    const { mutate: reqGetTemporaryAccess, isPending: isGettingTemporaryAccess } = useMutation({
        mutationFn: getTemporaryAccess,
        onSuccess: ({ accessToken, refreshToken, userId, accountId, accountName, userRole, userEmail, userFirstName, userLastName }) => {
            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
            // Remove refresh token as it's not needed for temporary access
            localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
            localStorage.setItem(STORAGE_KEYS.QUOTE, '');
            // update user cached details
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify({
                id: userId,
                firstName: userFirstName,
                lastName: userLastName,
                email: userEmail,
                role: userRole,
                accountId: accountId,
                accountName: accountName,
            }));
            // Redirect to the home page
            window.location.href = routes.root;
        },
        onError: (error: CustomMap) => {
            toast.error(error?.response?.data?.message || 'Failed to get temporary access');
            console.error('Temporary access error:', error);
        }
    });

    /**
     * A switch may cross the partner/end-customer boundary, so the landing route is
     * derived from the account being entered: sending an end-customer session to
     * `routes.root` (/leads) renders an empty page, because that route is not mounted
     * under the EC layout. The full page load also guarantees that no react-query
     * cache from the previous account survives.
     */
    const { mutate: reqSwitchAccount, isPending: isSwitchingAccount } = useMutation({
        mutationFn: switchAccount,
        onSuccess: (data) => {
            window.location.href = applySwitchedSession(localStorage, data);
        },
        onError: (error: CustomMap) => {
            toast.error(error?.response?.data?.message || 'Failed to switch account');
            console.error('Account switch error:', error);
        }
    });

    // Only worth opening for switching when there is somewhere else to go.
    const hasAccounts = useMemo(() => !!accounts && accounts.length > 1, [accounts]);
    const hasAllAccounts = useMemo(() => allAccounts && allAccounts?.accounts?.length > 0, [allAccounts]);

    const toggleOpen = useCallback(() => {
        setOpen(prev => !prev);
    }, []);

    const filteredAccounts = useMemo(() => {
        return accounts?.filter(account => account.accountName.toLowerCase().includes(search.toLowerCase()));
    }, [accounts, search]);

    const { partnerAccounts, customerAccounts } = useMemo(
        () => groupSwitchableAccounts(filteredAccounts || []),
        [filteredAccounts]
    );

    const allAccountsFiltered = useMemo(() => {
        return allAccounts?.accounts?.filter(account => account.accountName.toLowerCase().includes(search.toLowerCase()));
    }, [allAccounts, search]);

    const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    }, []);

    const handleAccountClick = useCallback((account: SwitchableAccount) => () => {
        if (account.isCurrent || isSwitchingAccount) {
            return;
        }

        setOpen(false);
        setSearch('');
        reqSwitchAccount(account.userId);
    }, [isSwitchingAccount, reqSwitchAccount]);

    const handleSuperAdminAccountClick = useCallback((account: Account) => () => {
        reqGetTemporaryAccess(account.id);
    }, [reqGetTemporaryAccess]);

    const renderAccountGroup = useCallback((label: string, group: SwitchableAccount[]) => {
        if (group.length === 0) {
            return null;
        }

        return (
            <>
                <div className={styles.splitter}>{label}</div>
                {group.map(account => (
                    <div
                        key={account.userId}
                        className={cn(styles.accountItem, { [styles.active]: account.isCurrent })}
                        onClick={handleAccountClick(account)}
                    >
                        <p>{account.accountName}</p>
                        <p className={styles.user}>{account.role.replace(/_/g, ' ').toLowerCase()}</p>
                    </div>
                ))}
            </>
        );
    }, [handleAccountClick]);

    return (
        <div className={styles.details}>
            <div className={styles.settingsBtn} onClick={hasAccounts || hasAllAccounts ? toggleOpen : undefined}>
                <div className={styles.accountName}>{accountName}</div>
                {(hasAccounts || hasAllAccounts) && <DownOutlined />}
            </div>

            {
                open && (
                    <div className={styles.dropdown}>
                        <label className={styles.label}>Account Name</label>
                        <Input placeholder="Search account" onChange={handleSearch} />
                        <div className={styles.accountList}>
                            {/*
                              * The user's OWN accounts come first. For a super admin the
                              * Temporary Access list is every account in the system (~400 on
                              * staging), which pushed these two groups ~1900px down a 300px
                              * scroll box — switching into your own end-customer account was
                              * effectively undiscoverable. Own accounts are few and are the
                              * common case; borrowing another account is the rarer one.
                              */}
                            {renderAccountGroup('Partner Accounts', partnerAccounts)}
                            {renderAccountGroup('Customer Accounts', customerAccounts)}

                            {
                                isSuperAdmin && allAccountsFiltered && allAccountsFiltered?.length > 0 && (
                                    <>
                                        <div className={styles.splitter}>Temporary Access</div>
                                        {
                                            allAccountsFiltered?.map(account => {
                                                if (account.id === accountId) {
                                                    return null;
                                                }

                                                return (
                                                    <div key={account.id} className={cn(styles.accountItem, { [styles.active]: account.id === accountId })} onClick={isGettingTemporaryAccess ? undefined : handleSuperAdminAccountClick(account)}>
                                                        <p>{account.accountName}</p>
                                                    </div>
                                                )
                                            })
                                        }
                                    </>
                                )
                            }
                        </div>
                    </div>
                )}
        </div>
    );
};

export default AccountDetails;
